import axios from "axios";
import i18n from "i18n/i18n";
import Uri from "jsuri";
import isEmpty from "lodash/isEmpty";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Button, Checkbox, Grid, Icons, Modal, Select } from "@texada/ui";

import { addMessage, logout, updateCurrentUser } from "actions";
import { USER_URL } from "constants/AppConstants";
import { ReduxState, WINDOW_EVENT } from "helpers/helper";
import useToggle from "helpers/hooks";
import { toDivisionTime } from "helpers/timeHelpers";

import { ContractArchiveModal } from "./ContractArchiveModal";
import QuickbarHeading from "./QuickbarHeading";
import UserInstance from "./UserInstance";

import "./Quickbar.scss";

const CLOCK_IN_VALUE = "ClockInValue";

function to2Digits(num: number) {
  return num.toLocaleString("en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false
  });
}

function formatDuration(startTime: moment.Moment) {
  if (startTime === null) {
    return null;
  }

  let differ = moment().unix() - startTime.unix();
  const seconds = differ % 60;
  // trim seconds
  differ -= seconds;
  // convert to minutes
  differ /= 60;
  const minutes = differ % 60;
  differ -= minutes;
  const hours = differ / 60;

  return `${to2Digits(hours)}:${to2Digits(minutes)}:${to2Digits(seconds)}`;
}

export function usePostClock() {
  const user = useSelector<
    ReduxState,
    { id: number; name: string; srm_uid: string }
  >(state => state.auth.user);
  return async () => {
    const start = toDivisionTime(
      moment(localStorage.ClockInValue).format(),
      ""
    );
    const end = toDivisionTime(moment().format(), "");

    await axios
      .post("/rest-api/clock/", {
        user_auth_id: user.id,
        user_name: user.name,
        user_srm_uid: user.srm_uid,
        timestamp: start,
        record_type: "1"
      })
      .then(async () => {
        await axios
          .post("/rest-api/clock/", {
            user_auth_id: user.id,
            user_name: user.name,
            user_srm_uid: user.srm_uid,
            timestamp: end,
            record_type: "0"
          })
          .then(() => {
            localStorage.removeItem(CLOCK_IN_VALUE);
          });
      });
  };
}

function useOnClockChange(onChange: (clock: string) => void) {
  const prevClock = useRef<string>();
  useEffect(() => {
    prevClock.current = localStorage.getItem(CLOCK_IN_VALUE);

    function onStorage() {
      const clock = localStorage.getItem(CLOCK_IN_VALUE);
      if (clock !== prevClock.current) {
        prevClock.current = clock;
        onChange(clock);
      }
    }

    window.addEventListener(WINDOW_EVENT.STORAGE, onStorage);
    return () => {
      window.removeEventListener(WINDOW_EVENT.STORAGE, onStorage);
    };
  }, []);
}

export function useDuration() {
  const postClock = usePostClock();

  const [duration, setDuration] = useState(() => {
    const clockInValue = localStorage.getItem(CLOCK_IN_VALUE);
    if (clockInValue !== null) {
      const storedClockIn = moment(clockInValue);
      return storedClockIn;
    }
    return null;
  });
  const [message, setMessage] = useState(null);

  useOnClockChange(time => {
    setDuration(time === null ? null : moment(time));
  });

  return {
    duration,
    message,
    clockIn() {
      const time = moment();
      localStorage.setItem(CLOCK_IN_VALUE, time.toString());
      setDuration(time);
    },
    async clockOut() {
      try {
        await postClock();
        setDuration(null);

        setMessage({
          success: true,
          info: i18n.t("time_saved")
        });
        window.setTimeout(() => {
          setMessage(null);
        }, 3000);
      } catch {
        setMessage({
          success: false,
          info: i18n.t("failed")
        });
        window.setTimeout(() => {
          setMessage(null);
        }, 3000);
      }
    }
  };
}

function UserClock({
  duration,
  message,
  clockIn,
  clockOut
}: ReturnType<typeof useDuration>) {
  const [elapsedTime, setElapsedTime] = useState(() =>
    formatDuration(duration)
  );

  useEffect(() => {
    if (duration === null) return;

    function refreshDuration() {
      setElapsedTime(formatDuration(duration));
    }
    const interval = window.setInterval(refreshDuration, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [duration]);

  return (
    <div className="quickbar-user-clock">
      <Button
        className={`quickbar-button clocked-${duration ? "in" : "out"}`}
        icon={{
          type: "left",
          render: duration ? Icons.Stop : Icons.Next
        }}
        onClick={() => {
          duration ? clockOut() : clockIn();
        }}
      >
        {duration ? i18n.t("clock_out") : i18n.t("clock_in")}
      </Button>

      <span
        className="quickbar-clocked-message"
        data-testid="quickbar-clocked-message"
      >
        {message !== null && message.info}
        {elapsedTime}
      </span>
    </div>
  );
}

const QuickbarUser: React.FC<{
  isOpen: boolean;
  onCancel: () => void;
  setIsQuickbarOpen: (isOpen: string) => void;
}> = props => {
  const user = useSelector<ReduxState, any>((state: any) => state.auth.user);

  const postClock = usePostClock();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSRM, setLoadingSRM] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [selectedDivision, setSelectedDivision] = useState();
  const [isDivisionModalOpen, setIsDivisionModalOpen] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [divisionError, setDivisionError] = useState("");
  const { duration, message, clockIn, clockOut } = useDuration();
  const { showContractArchive, openContractArchive, closeContractArchive } =
    useToggle({ name: "contractArchive" });
  const hasArchiveContractPermission =
    (user?.permissions?.admin && user.upgraded) || user?.contract_archive;

  const openSrmManagement = async division => {
    setLoadingSRM(true);
    const url = new Uri("/rest-api/srmlogin/");
    if (division) {
      url.addQueryParam("division", division.warehouse.srm_uid.trim());
      if (saveAsDefault) {
        await axios.patch(`${USER_URL}${user.id}/`, {
          default_division: division.warehouse.id,
          company: user.company
        });
        dispatch(updateCurrentUser());
      }
    }

    try {
      const loginURL = await axios.get(url.toString());
      window.open(loginURL.data, "_blank");
    } catch {
      dispatch(
        addMessage({
          type: "error",
          text: i18n.t("could_not_login_to_srm")
        })
      );
    } finally {
      setLoadingSRM(false);
      setSelectedDivision();
      setIsDivisionModalOpen(false);
      props.setIsQuickbarOpen("false");
    }
  };

  useEffect(() => {
    if (user.upgraded) {
      setCompanyName(user?.userinstance?.current?.name);
      return;
    }
    setCompanyName(user.company_title);
  }, [user]);

  function switchToSrm() {
    if (!user.upgraded || !user.compperms.operator_by_division) {
      openSrmManagement();
      return;
    }

    let defaultDivision = user.divisions.find(e => e.is_default);
    if (isEmpty(defaultDivision)) {
      props.setIsQuickbarOpen("false");
      setIsDivisionModalOpen(true);
      return;
    }

    openSrmManagement(defaultDivision);
  }

  const divisionOptions = user?.divisions
    ?.filter(w => w.is_user_division)
    .map(w => ({
      ...w.warehouse,
      label: w.warehouse.title,
      value: w.warehouse.srm_uid || " "
    }));
  return (
    <>
      <Modal
        {...props}
        header={i18n.t("user_menu")}
        size="quickbar"
        shouldReturnFocusAfterClose={false}
        closeButton
        isLoading={isLoading}
      >
        <Modal.Body>
          <div className="quickbar-user-top">
            <QuickbarHeading
              size="default"
              className={duration ? "clocked-in" : "clocked-out"}
              heading={user.name}
              subHeading={
                duration ? i18n.t("clocked_in") : i18n.t("clocked_out")
              }
              icon={{ render: Icons.Face }}
            />
            <QuickbarHeading
              className="quickbar-company"
              size="default"
              heading={companyName}
              icon={{ render: Icons.Business }}
            />
          </div>
          <UserClock
            duration={duration}
            message={message}
            clockIn={clockIn}
            clockOut={clockOut}
          />

          {user?.permissions?.admin && (
            <Button
              as={Link}
              buttonProps={{ to: "/setting" }}
              className="quickbar-button"
              icon={{ type: "left", render: Icons.Settings }}
              onClick={() => {
                props.setIsQuickbarOpen("false");
              }}
            >
              {i18n.t("settings")}
            </Button>
          )}

          <Button
            className="quickbar-button"
            icon={{ type: "left", render: Icons.Dvr }}
            onClick={switchToSrm}
            isLoading={loadingSRM}
          >
            {i18n.t("switch_to_srm")}
          </Button>

          {user?.companyFeatures?.contract_archive &&
          hasArchiveContractPermission ? (
            <Button
              className="quickbar-button"
              icon={{ type: "left", render: Icons.Archive }}
              onClick={openContractArchive}
            >
              {i18n.t("contract_archive")}
            </Button>
          ) : (
            <></>
          )}
          <UserInstance
            setIsLoading={setIsLoading}
            duration={duration}
            clockOut={clockOut}
          />

          <Button
            className="quickbar-button"
            icon={{ type: "left", render: Icons.Exit }}
            onClick={async () => {
              if (duration === null) {
                dispatch(logout());
              } else {
                await postClock().finally(() => {
                  dispatch(logout());
                });
              }
            }}
          >
            {i18n.t("log_out")}
          </Button>
        </Modal.Body>
      </Modal>
      <Modal
        isOpen={isDivisionModalOpen}
        header={i18n.t("select_division")}
        onCancel={() => setIsDivisionModalOpen(false)}
        isLoading={loadingSRM}
      >
        <Modal.Body padded={false}>
          <Grid.Row>
            <Grid.Col>
              <Select
                label={i18n.t("division")}
                required
                placeholder={i18n.t("select_division")}
                value={selectedDivision?.value}
                options={divisionOptions}
                onChange={seleted => {
                  const target = user.divisions.find(
                    e => e.warehouse.srm_uid === seleted.target.value.trim()
                  );
                  setSelectedDivision({
                    ...target,
                    value: seleted.target.value
                  });
                }}
                error={divisionError}
                clearable={false}
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col>
              <Checkbox
                name="checkbox"
                label={i18n.t("set_as_default_division_for_future_logins")}
                checked={saveAsDefault}
                onChange={e => {
                  setSaveAsDefault(e.target.value);
                }}
              />
            </Grid.Col>
          </Grid.Row>
        </Modal.Body>
        <Modal.Footer
          saveButton
          saveButtonText={i18n.t("switch_to_srm")}
          closeButton
          onSubmit={() => {
            if (isEmpty(selectedDivision)) {
              setDivisionError(
                i18n.t("field_is_required", { field: i18n.t("division") })
              );
              return;
            }
            openSrmManagement(selectedDivision);
          }}
        />
      </Modal>
      <ContractArchiveModal
        isOpen={showContractArchive}
        onCancel={closeContractArchive}
      />
    </>
  );
};

export default QuickbarUser;
